import { Button, Stack } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useFoerderProgramm } from 'src/context/FoerderProgrammContext';
import { ANTRAG_TYP, ANTRAGS_FORMULAREN, ANTRAGS_VERFAHREN, GEGENSTAND_DER_FOERDERUNG, SKIZZE_UND_ANTRAG_EXTERN } from 'src/lib/constants/constant';
import { enumT, isAntragstellerHAW, isObjectEmpty, isOrgaAbteilungEinerHochschule, isTrue } from 'src/utils/helper';

// import Schemas
import FoerderProgrammSchema from 'src/lib/paths/foerderprogramm_FULL_Paths.json';
import FoerderRichtnilie from 'src/lib/paths/foerderrichtlinie_FULL_Paths.json';
import OrganisationSchema from 'src/lib/paths/organisation_FULL_Paths.json';
const FP_P = FoerderProgrammSchema.attributes;
const FR_P = FoerderRichtnilie.attributes;
const OP_P = OrganisationSchema.attributes;

// This component renders the buttons to create an application for the Förderprogramm
// It uses the antragsInformation from the Förderprogramm context to render the buttons
// Each button corresponds to an antragsformular and its respective antragsverfahren
// If the antragsformular requires a special handling (like Freischaltung), it will render a special button
// If the antragsformular is not in CUSTOM_ANTRAGS_FORMULAREN, it will render a button based on the antragsVerfahren
const CreateAntragButtons: FC<{}> = () => {
	const { foerderRichtlinieDetails, organisationDetails } = useFoerderProgramm();
	const foerderProgramm = get(foerderRichtlinieDetails, FR_P.Foerderrichtlinie_Foerderprogramms.path, []);

	// const antragsInformation: any[] = get(foerderRichtlinieDetails, FP_P.Antragsinformationen.path, []);

	/**
	 * *********************************** BITTE ZUERST HA-2425 DURCHLESEN ***********************************
	 * Laut das Ticket HA-2425, sollen die Buttons nur für HAWs angezeigt werden, wenn die Organisation eine Abteilung einer Hochschule ist.
	 * Daher wird hier geprüft, ob die Organisation eine Abteilung einer Hochschule ist und ob der Antragsteller eine HAW ist.
	 */
	//
	const isAbteilung = isOrgaAbteilungEinerHochschule(organisationDetails);
	if (isTrue(isAbteilung)) {
		// wenn die Hochschule bzw Abteilung Einer Organisation ist, dann überhaupt nichts (Keine Buttons) angezeigt werden.0
		if (!isTrue(isAntragstellerHAW(organisationDetails))) {
			return <></>;
		}
	}

	return (
		<Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
			{foerderProgramm
				.filter((eachFP: any) => isTrue(get(eachFP, FP_P.Allgemein_Aktiv.path)))
				.map((ele: any, i: number) => {
					const currentDatum = new Date();
					currentDatum.setHours(0, 0, 0, 0);
					const bisWannEinreichenMoeglich = get(ele, FP_P.Bis_Wann_Einreichung_Moeglich.path, '');
					// Check if the current date is before the bisWannEinreichenMoeglich date
					if (bisWannEinreichenMoeglich) {
						const bisWannEinreichenMoeglichDate = new Date(bisWannEinreichenMoeglich);
						bisWannEinreichenMoeglichDate.setHours(0, 0, 0, 0);
						if (currentDatum > bisWannEinreichenMoeglichDate) {
							return null;
						}
					}
					return <RenderButtons key={i} foerderProgrammID={get(ele, 'id', -1)} foerderProgrammDetails={ele} />;
				})}
		</Stack>
	);
};

export default CreateAntragButtons;

const RenderButtons: FC<{ foerderProgrammID: number; foerderProgrammDetails: any }> = ({ foerderProgrammID, foerderProgrammDetails }) => {
	const { organisationDetails, handleCreateApplication, handleShowFreischaltungsPopUp, handleShowSkizzeUeberspringenPopUp } = useFoerderProgramm();

	// Antrags-Formular
	const antragsFormular = get(foerderProgrammDetails, FP_P.Allgemein_Antragsformular.path, '');
	const antragsVerfahren = get(foerderProgrammDetails, FP_P.Antragsinformationen_Antragsverfahren.path, '');

	// Extract the button name from the antragsFormular
	// If the antragsFormular is not in CUSTOM_ANTRAGS_FORMULAREN, use the original name
	// const originalButtonName = extractTextInParentheses(enumT(antragsFormular));
	// const anzeigeButtonName = `Förderung ${get(CUSTOM_ANTRAGS_FORMULAREN, originalButtonName, originalButtonName)} beantragen`;
	const originalButtonName = get(foerderProgrammDetails, FP_P.Allgemein_Kurztitel.path, enumT(antragsFormular));
	const anzeigeButtonName =
		antragsFormular === ANTRAGS_FORMULAREN.Loewe ? '1. Stufe: LOEWE Skizze erstellen' : `Förderung ${originalButtonName} beantragen`;

	// Check if the antragsformular requires a special handling
	// Some Antragsformulare must be approved before application creation
	const showSkizzeUeberspringen = useMemo(() => {
		const skizzeUeberspringen = isTrue(get(foerderProgrammDetails, FP_P.Antragsinformationen_Ueberspringen_Skizze_Erlaubt.path));

		return skizzeUeberspringen;
	}, [foerderProgrammDetails]);

	// Check if the antragsformular requires a special handling
	// Some Antragsformulare must be approved before application creation
	const showFreischaltungButton = useMemo(() => {
		const antragErstelltungNurPerFreischaltung = isTrue(get(foerderProgrammDetails, FP_P.Antragstellung_Nur_Per_Freischaltung.path));
		const freigeschalteteAntraege = get(organisationDetails, OP_P.Freischaltung_Je_Programm.path, []);

		const isFreiGeschaltet = freigeschalteteAntraege.find((ele: any) => get(ele, 'Programm', '') === antragsFormular);

		// isFreiGeschaltet kann undefined sein, wenn nichts gefunden wurde
		return antragErstelltungNurPerFreischaltung && (isFreiGeschaltet === undefined || isObjectEmpty(isFreiGeschaltet));
	}, [antragsFormular, foerderProgrammDetails, organisationDetails]);

	// Check if Antragstellung is gesperrt
	// If gesperrt, show a disabled button
	if (isTrue(get(foerderProgrammDetails, FP_P.Antragstellung_Gesperrt.path))) {
		return (
			<Button sx={{ maxWidth: 'fit-content', pointerEvents: 'unset !important', cursor: 'not-allowed !important' }} disabled>
				{anzeigeButtonName}
			</Button>
		);
	}

	// If the button is for Freischaltung, show the Freischaltung button
	// the Fresischaltung button will open a dialog to request the Freischaltung
	if (showFreischaltungButton) {
		return (
			<>
				<Button
					onClick={() => {
						handleShowFreischaltungsPopUp(
							get(foerderProgrammDetails, FP_P.Allgemein_Antragsformular.path, ''), // antragsFormular,
							foerderProgrammDetails, // Selected foerderprogramm
						);
					}}
				>
					Freischaltung beantragen
				</Button>
			</>
		);
	}

	let buttonsToRender = [];

	const isSkizzeExtern = get(foerderProgrammDetails, FP_P.Konfiguration_Antragsbearbeitung.path, '') === SKIZZE_UND_ANTRAG_EXTERN.SkizzeExtern;
	const antragstyp =
		antragsVerfahren === ANTRAGS_VERFAHREN.zweistufig ?
			isSkizzeExtern ? ANTRAG_TYP.Antrag
			:	ANTRAG_TYP.Skizze
		:	ANTRAG_TYP.Antrag;
	const isLoewe = antragsFormular === ANTRAGS_FORMULAREN.Loewe;
	const skizzeUeberspringenButton = (
		<>
			<Button
				onClick={() => {
					handleShowSkizzeUeberspringenPopUp(foerderProgrammDetails);
				}}
			>
				{isLoewe ? '2. Stufe: LOEWE Antrag stellen' : 'Skizze überspringen'}
			</Button>
		</>
	);

	// If the button is for Freischaltung, show the Freischaltung button
	// the Fresischaltung button will open a dialog to request the Freischaltung
	if (showSkizzeUeberspringen && !isLoewe) {
		buttonsToRender.push(skizzeUeberspringenButton);
	}
	// If the antragsformular is not in CUSTOM_ANTRAGS_FORMULAREN, render the button based on the antragsVerfahren
	switch (antragsFormular) {
		case ANTRAGS_FORMULAREN.Mobilitaetsplaene:
			buttonsToRender.push(
				<>
					<Button
						sx={{ maxWidth: 'fit-content' }}
						onClick={() => {
							const antragDetailObject = {
								AntragsTyp: ANTRAG_TYP.Antrag,
								Antragsformular: ANTRAGS_FORMULAREN.Mobilitaetsplaene,
								GegenStand: GEGENSTAND_DER_FOERDERUNG.Erstellung,
							};
							handleCreateApplication(foerderProgrammID, foerderProgrammDetails, antragDetailObject);
						}}
					>
						für Erstellung beantragen
					</Button>
					<Button
						sx={{ maxWidth: 'fit-content' }}
						onClick={() => {
							const antragDetailObject = {
								AntragsTyp: ANTRAG_TYP.Antrag,
								Antragsformular: ANTRAGS_FORMULAREN.Mobilitaetsplaene,
								GegenStand: GEGENSTAND_DER_FOERDERUNG.Umsetzung,
							};
							handleCreateApplication(foerderProgrammID, foerderProgrammDetails, antragDetailObject);
						}}
					>
						für Umsetzung beantragen
					</Button>
				</>,
			);
			break;
		case ANTRAGS_FORMULAREN.Elektrobusse:
			buttonsToRender.push(
				<>
					<Button
						sx={{ maxWidth: 'fit-content' }}
						onClick={() => {
							const antragDetailObject = {
								AntragsTyp: ANTRAG_TYP.Antrag,
								Antragsformular: ANTRAGS_FORMULAREN.Elektrobusse,
								GegenStand: GEGENSTAND_DER_FOERDERUNG.Beschaffung,
							};
							handleCreateApplication(foerderProgrammID, foerderProgrammDetails, antragDetailObject);
						}}
					>
						für Beschaffung beantragen
					</Button>
					<Button
						sx={{ maxWidth: 'fit-content' }}
						onClick={() => {
							const antragDetailObject = {
								AntragsTyp: ANTRAG_TYP.Antrag,
								Antragsformular: ANTRAGS_FORMULAREN.Elektrobusse,
								GegenStand: GEGENSTAND_DER_FOERDERUNG.Nachruestung,
							};
							handleCreateApplication(foerderProgrammID, foerderProgrammDetails, antragDetailObject);
						}}
					>
						für Nachrüstung beantragen
					</Button>
				</>,
			);
			break;
		case ANTRAGS_FORMULAREN.LogistikUndMobilitaet:
			// case ANTRAGS_FORMULAREN.StartUpStipendium:
			// For this antragsformular, the button is always disabled
			// beacause it is created somwwhere else
			buttonsToRender.push(
				<Button sx={{ maxWidth: 'fit-content', pointerEvents: 'unset !important', cursor: 'not-allowed !important' }} disabled>
					{anzeigeButtonName}
				</Button>,
			);
			break;
		default:
			buttonsToRender.push(
				<Button
					sx={{ maxWidth: 'fit-content' }}
					onClick={() => {
						const antragDetailObject = {
							AntragsTyp: antragstyp,
							Antragsformular: antragsFormular,
							GegenStand: '',
							foerderProgrammID: foerderProgrammID,
						};
						handleCreateApplication(foerderProgrammID, foerderProgrammDetails, antragDetailObject);
					}}
				>
					{anzeigeButtonName}
				</Button>,
			);
			break;
	}
	if (showSkizzeUeberspringen && isLoewe) {
		buttonsToRender.push(skizzeUeberspringenButton);
	}
	return <>{buttonsToRender}</>;
};
